import { FIELDS, LEVELS, parse, resolveField } from '@fleequid-logs/query';

// The query bar's autocompletion: the token under the caret, and what could complete it. Fields come from the query
// language itself; values from the facets of the query on screen, so they exist and carry their counts.

export interface Token {
    start: number;
    end: number;
    text: string;
}

export type SuggestionKind = 'field' | 'value' | 'recent';

export interface Suggestion {
    kind: SuggestionKind;
    // What replaces the token (a field ends with ':' and keeps the list open for its values).
    insert: string;
    label: string;
    detail?: string;
    count?: number;
}

export interface SuggestContext {
    facets: Readonly<Record<string, ReadonlyArray<{ value: string; count: number }>>> | undefined;
    envs: readonly string[];
    recent: readonly string[];
}

const MAX = 8;
const KINDS = ['log', 'request', 'exception'];
const HAS = { name: 'has', description: 'Lines where a field is set: has:user, has:job' };

export const quoteValue = (value: string): string => (/[\s()"]/.test(value) || value === '' ? `"${value.replace(/"/g, '\\"')}"` : value);

// The token the caret is in: from the last space or parenthesis before it to the next one after it, a quoted value
// (`module:"Cron Jo|`) whole.
export function tokenAt(text: string, caret: number): Token {
    const before = text.slice(0, caret);
    const insideQuotes = (before.match(/"/g) ?? []).length % 2 === 1;
    let start = insideQuotes ? before.lastIndexOf('"') : caret;
    while (start > 0 && !/[\s(]/.test(text[start - 1] as string)) {
        start--;
    }
    let end = caret;
    if (insideQuotes) {
        const close = text.indexOf('"', caret);
        end = close < 0 ? text.length : close + 1;
    } else {
        while (end < text.length && !/[\s)]/.test(text[end] as string)) {
            end++;
        }
    }

    return { start, end, text: text.slice(start, end) };
}

const rank = (candidate: string, prefix: string): number => {
    const lower = candidate.toLowerCase();
    if (prefix === '') {
        return 1;
    }

    return lower.startsWith(prefix) ? 2 : lower.includes(prefix) ? 1 : 0;
};

function valuesFor(field: string, context: SuggestContext): Array<{ value: string; count?: number; detail?: string }> {
    if (field === 'level') {
        return [
            ...[...LEVELS].reverse().map((level) => ({ value: level })),
            { value: '>=warn', detail: 'warnings and worse' },
            { value: '>=error', detail: 'errors and worse' },
        ];
    }
    if (field === 'kind') {
        return KINDS.map((value) => ({ value, count: context.facets?.kind?.find((item) => item.value === value)?.count }));
    }
    if (field === 'env') {
        return context.envs.map((value) => ({ value }));
    }
    if (field === 'has') {
        return FIELDS.filter((item) => !['message', 'env', 'source', 'level'].includes(item.name)).map((item) => ({
            value: item.name,
            detail: item.description,
        }));
    }

    return (context.facets?.[field] ?? []).map((item) => ({ value: item.value, count: item.count }));
}

export function suggest(text: string, caret: number, context: SuggestContext): { token: Token; items: Suggestion[] } {
    const token = tokenAt(text, caret);
    if (text.trim() === '') {
        return {
            token: { start: 0, end: text.length, text },
            items: context.recent.slice(0, MAX).map((query) => ({ kind: 'recent', insert: query, label: query })),
        };
    }
    const negated = token.text.startsWith('-');
    const body = negated ? token.text.slice(1) : token.text;
    const sign = negated ? '-' : '';
    const colon = body.indexOf(':');

    if (colon < 0) {
        // A bare word: the fields it could start. Free text stays free text when nothing is chosen.
        const prefix = body.toLowerCase();
        if (prefix === '' || body.startsWith('"') || body.startsWith('/')) {
            return { token, items: [] };
        }
        const items = [...FIELDS, HAS]
            .map((field) => ({ field, score: rank(field.name, prefix) }))
            .filter(({ field, score }) => score > 0 && field.name !== prefix)
            .sort((a, b) => b.score - a.score)
            .slice(0, MAX)
            .map(({ field }) => ({ kind: 'field' as const, insert: `${sign}${field.name}:`, label: `${field.name}:`, detail: field.description }));

        return { token, items };
    }

    const typed = body.slice(0, colon);
    const rawValue = body.slice(colon + 1);
    let field: string;
    try {
        const resolved = typed.toLowerCase() === 'has' ? null : resolveField(typed);
        field = resolved === null ? 'has' : resolved.kind === 'column' ? resolved.field.name : '';
    } catch {
        return { token, items: [] };
    }
    if (field === '') {
        return { token, items: [] };
    }
    const prefix = rawValue.replace(/^"/, '').replace(/"$/, '').toLowerCase();
    const items = valuesFor(field, context)
        .map((item) => ({ item, score: rank(item.value, prefix) }))
        .filter(({ item, score }) => score > 0 && item.value.toLowerCase() !== prefix)
        .sort((a, b) => b.score - a.score || (b.item.count ?? 0) - (a.item.count ?? 0))
        .slice(0, MAX)
        .map(({ item }) => ({
            kind: 'value' as const,
            insert: `${sign}${typed}:${item.value.startsWith('>') ? item.value : quoteValue(item.value)} `,
            label: item.value,
            detail: item.detail,
            count: item.count,
        }));

    return { token, items };
}

// A query still being typed: a quote or a bracket left open, or a field waiting for its value at the very end
// (`service:`, `has:`, `request.headers.host:`). The bar does not run it until Enter. A word that only looks like a
// field (`Exception for:`) is text, as the query language reads it.
export function isIncomplete(text: string): boolean {
    if (parse(text).errors.length > 0) {
        return true;
    }
    const match = /(?:^|[\s(])-?([\w.@$-]+):$/.exec(text);
    if (!match) {
        return false;
    }
    const name = match[1] as string;
    if (name.toLowerCase() === 'has' || name.includes('.')) {
        return true;
    }
    try {
        return resolveField(name).kind === 'column';
    } catch {
        return false;
    }
}

// The text with the token replaced, and where the caret goes: after what was inserted.
export function applySuggestion(text: string, token: Token, insert: string): { text: string; caret: number } {
    const before = text.slice(0, token.start);
    let after = text.slice(token.end);
    if (insert.endsWith(' ') && after.startsWith(' ')) {
        after = after.slice(1);
    }

    return { text: before + insert + after, caret: before.length + insert.length };
}

// Recent queries, newest first, remembered by the browser.
const HISTORY_KEY = 'fl.history';

export function loadHistory(): string[] {
    try {
        const stored = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]') as unknown;

        return Array.isArray(stored) ? stored.filter((item): item is string => typeof item === 'string').slice(0, 20) : [];
    } catch {
        return [];
    }
}

export function remember(history: readonly string[], query: string): string[] {
    const trimmed = query.trim();
    if (trimmed === '') {
        return [...history];
    }
    const next = [trimmed, ...history.filter((item) => item !== trimmed)].slice(0, 20);
    try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    } catch {
        // Kept for this page only.
    }

    return next;
}
